<template>
    <tr class="attachment"
        :class="{
            'is-editing': isEditing || isRemoving,
            'can-edit': canEdit,
        }"
        @keyup.enter="save"
        @keyup.esc="close"
    >
        <td @click="download" class="has-text-centered" width="2em" v-if="!isImage">
            <span class="icon">
                <i class="mdi mdi-24px" :class="'mdi-' + icon"></i>
            </span>
        </td>
        <td @click="download" ref="name"
            :colspan="isImage ? 3 + (showAuthor ? 1 : 0) : 1"
            v-if="!isRemoving || isImage">
            <div v-if="isImage" class="image-preview">
                <div class="remove-cover" v-if="isRemoving">
                    <button class="button is-danger"
                            :class="{'is-loading': isRemovingConfirmed}"
                            :disabled="isRemovingConfirmed"
                            @click="confirmRemove">
                        <span>Подтвердить удаление</span>
                    </button>
                </div>
                <img :src="this.attachment.url" :alt="title">
            </div>
            <template v-else>
                <template v-if="isEditing">
                    <b-input v-focus
                            size="is-small"
                            expanded
                            :disabled="isSaving"
                            v-model="attachment.title"/>
                </template>
                <template v-else>
                    <p class="name">
                        <span class="is-size-7">{{ title }}</span>
                    </p>
                </template>
            </template>
        </td>
        <td @click="download" v-if="!isRemoving && showAuthor && !isImage">
            <p>
                <span class="is-size-7">{{ author }}</span>
            </p>
        </td>
        <td @click="download"
            width="5em"
            v-if="!isRemoving && !isImage">
            <p>
                <span class="is-size-7">{{ size }}</span>
            </p>
        </td>
        <td v-if="isRemoving && !isImage" colspan="3">
            <p class="control">
                <button class="button is-danger is-small is-fullwidth"
                        :class="{'is-loading': isRemovingConfirmed}"
                        :disabled="isRemovingConfirmed"
                        @click="confirmRemove">
                    <span>Подтвердить удаление {{ title }}</span>
                </button>
            </p>
        </td>
        <td class="has-text-centered"
            width="5em"
            v-if="canManage">
            <b-field class="actions" grouped>
                <template v-if="isEditing">
                    <p class="control">
                        <button class="button is-outlined is-small"
                                :disabled="isSaving"
                                @click="close">
                            <b-icon icon="close" size="is-small"></b-icon>
                        </button>
                    </p>
                    <p class="control">
                        <button class="button is-success is-outlined is-small"
                                :class="{'is-loading': isSaving}"
                                :disabled="!canSave || isSaving"
                                @click="save">
                            <b-icon icon="check" size="is-small"></b-icon>
                        </button>
                    </p>
                </template>
                <template v-else>
                    <p class="control" v-if="!isImage">
                        <button class="button is-outlined is-small"
                                :disabled="isRemoving"
                                @click="open">
                            <b-icon icon="pencil" size="is-small"></b-icon>
                        </button>
                    </p>
                    <p class="control" v-if="isRemoving">
                        <button class="button is-danger is-outlined is-small"
                                @click="close">
                            <b-icon icon="close" size="is-small"></b-icon>
                        </button>
                    </p>
                    <p class="control" v-else>
                        <button class="button is-danger is-outlined is-small"
                                :class="{'is-loading': isRemovingConfirmed}"
                                :disabled="isRemovingConfirmed"
                                @click="remove">
                            <b-icon icon="delete" size="is-small"></b-icon>
                        </button>
                    </p>
                </template>
            </b-field>
        </td>
    </tr>
</template>

<script>
import toasts from "../mixins/toasts";

export default Vue.extend({
  props: {
    value: {
      type: Object,
      required: true
    },
    showAuthor: {
      type: Boolean,
      default: true
    },
    canManage: {
      type: Boolean
    },
    canEdit: {
      type: Boolean
    }
  },
  mixins: [toasts],
  data() {
    return {
      attachment: {},
      original: {},
      isSaving: false,
      isRemoving: false,
      isRemovingConfirmed: false,
      isEditing: false
    };
  },
  computed: {
    icon() {
      switch (String(this.attachment.extension).toLowerCase()) {
        case "txt": {
          return "file-document";
        }
        case "pdf": {
          return "file-pdf";
        }
        case "doc":
        case "docx":
        case "docm": {
          return "file-word";
        }
        case "xls":
        case "xlsx":
        case "xlsm":
        case "xlsb": {
          return "file-excel";
        }
        case "ppt":
        case "pptx":
        case "pptm": {
          return "file-powerpoint";
        }
        case "bmp":
        case "gif":
        case "png":
        case "jpg":
        case "jpeg": {
          return "file-image";
        }
        default: {
          return "file";
        }
      }
    },
    isImage() {
      switch (String(this.attachment.extension).toLowerCase()) {
        case "bmp":
        case "gif":
        case "png":
        case "jpg":
        case "jpeg": {
          return true;
        }
        default: {
          return false;
        }
      }
    },
    title() {
      return _.truncate(this.attachment.title, {
        length: 36
      });
    },
    author() {
      return _.get(this.attachment, "user.short_name");
    },
    size() {
      return this.attachment.readable_size;
    },
    canSave() {
      return (
        _.trim(this.original.title) != _.trim(this.attachment.title) &&
        this.attachment.title.length > 0
      );
    }
  },
  created() {
    if (!this.value.id) {
      console.error("Attachment must have an id.");
    }

    this.attachment = this.value;
    this.original = _.clone(this.attachment);
  },
  methods: {
    download() {
      if (this.isEditing || this.isRemoving) {
        return;
      }
      window.open(this.attachment.url);
    },
    open() {
      if (!this.canEdit) {
        return;
      }
      this.isEditing = true;
      this.original = _.clone(this.attachment);
      this.$nextTick(_ => {
        let input = this.$refs.name.querySelector("input");
        if (input) {
          input.focus();
        }
        this.$emit("open", this.attachment);
      });
    },
    close() {
      if (this.isRemoving) {
        this.isRemoving = false;
      } else {
        this.isEditing = false;
        this.attachment.title = this.original.title;
        this.$emit("close", this.attachment);
      }
    },
    save() {
      if (!this.canSave) {
        this.close();
        return;
      }
      this.isSaving = true;
      _.delay(_ => {
        axios
          .put(`/api/attachments/${this.attachment.id}`, this.attachment)
          .then(response => {
            this.attachment = response.data;
          })
          .catch(error => {
            // Revert changes back
            this.attachment.title = this.original.title;
            switch (error.response.status) {
              case 403: {
                break;
              }
              default: {
                this.toast("error", "@unexpected", error);
              }
            }
          })
          .finally(_ => {
            this.isSaving = false;
            this.isEditing = false;
            this.$emit("save", this.attachment);
          });
      }, 500);
    },
    remove() {
      if (!this.canEdit) {
        return;
      }
      this.isRemoving = true;
    },
    confirmRemove() {
      if (!this.isRemoving) {
        return;
      }
      this.isRemovingConfirmed = true;
      _.delay(_ => {
        axios
          .delete(`/api/attachments/${this.attachment.id}`)
          .then(response => {
            this.isEditing = false;
          })
          .catch(error => {
            switch (error.response.status) {
              case 403: {
                break;
              }
              default: {
                this.toast("error", "@unexpected", error);
              }
            }
            this.isEditing = false;
          })
          .finally(_ => {
            this.isRemoving = false;
            this.isRemovingConfirmed = false;
            this.$emit("remove", this.attachment);
          });
      }, 500);
    }
  }
});
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/attachments/Attachment.vue